import React from 'react';
import { InView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';

import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';
import { BaseUser } from '@common/react/objects/BaseUser';
import { BaseApplicationState } from '@common/react/store';

import defaultVetAvatar from '@app/images/vetDefAvatar.svg';
import { getImagePath } from '@app/objects/Image';
import '@app/scss/components/customAvatar.scss';

interface CustomAvatarProps {
	src: string | undefined | null;
	mobileSrc?: string | undefined | null;
	alt?: string;
	errorAvatar?: any;
	wrapClassName?: string;
	className?: string;
	maxWidth?: number;
	maxHeight?: number;
	lazy?: boolean;
	offset?: number;
	fetchPriority?: 'high' | 'low' | 'auto';
	onClick?: () => void;
}

const CustomAvatar: React.FC<CustomAvatarProps> = ({
	src,
	mobileSrc,
	alt = 'Avatar',
	errorAvatar = defaultVetAvatar,
	wrapClassName = '',
	className = '',
	maxWidth,
	maxHeight,
	lazy = true,
	offset = 300,
	fetchPriority,
	onClick,
}) => {
	const hostOptions = useSelector((state: BaseApplicationState<BaseUser>) => state.hostOptions.item);
	const timerRef = React.useRef<NodeJS.Timeout | null>(null);
	const imgRef = React.useRef<HTMLImageElement | null>(null);

	const buildPath = (value?: string | null): string => getImagePath(value, maxHeight, maxWidth, hostOptions?.hostServer || '');

	const [desktopImgSrc, setDesktopImgSrc] = React.useState<string>(buildPath(src));
	const [mobileImgSrc, setMobileImgSrc] = React.useState<string | undefined>(mobileSrc ? buildPath(mobileSrc) : undefined);

	const [hasError, setHasError] = React.useState<boolean>(false);
	const [isLoaded, setIsLoaded] = React.useState<boolean>(false);
	const [isTimeout, setIsTimeout] = React.useState<boolean>(false);
	const [isVisible, setIsVisible] = React.useState<boolean>(!lazy);

	const clearTimer = () => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	};

	React.useEffect(() => {
		setDesktopImgSrc(buildPath(src));
		setMobileImgSrc(mobileSrc ? buildPath(mobileSrc) : undefined);
		setHasError(false);
		setIsLoaded(false);
		setIsTimeout(false);
		setIsVisible(!lazy);
		clearTimer();
	}, [src, maxWidth, maxHeight, lazy]);

	React.useEffect(() => {
		if (isVisible && !isLoaded && !hasError && !isTimeout && !timerRef.current) {
			timerRef.current = setTimeout(() => {
				setIsTimeout(true);
			}, 5000);
		}

		return () => clearTimer();
	}, [isVisible, isLoaded, hasError, isTimeout]);

	React.useEffect(() => {
		if (imgRef?.current?.complete) {
			setIsLoaded(true);
		}
	}, [desktopImgSrc, mobileImgSrc]);

	const showErrorImage = hasError || isTimeout;
	const showLoader = !isLoaded && !showErrorImage;

	const renderImage = () => (
		<>
			<picture>
				{mobileImgSrc && (
					<source
						media="(max-width: 768px)"
						srcSet={mobileImgSrc}
					/>
				)}

				<img
					ref={imgRef}
					src={desktopImgSrc}
					alt={alt}
					className={className}
					style={{
						display: !showErrorImage && isLoaded ? 'block' : 'none',
					}}
					onLoad={() => {
						setIsLoaded(true);
						clearTimer();

						if (isTimeout) setIsTimeout(false);
					}}
					onError={() => {
						setHasError(true);
						clearTimer();
					}}
					onClick={onClick}
					fetchPriority={fetchPriority}
				/>
			</picture>

			{showErrorImage && (
				<img
					src={errorAvatar}
					alt={alt}
					className={className}
					style={{ display: 'block' }}
					onClick={onClick}
				/>
			)}
		</>
	);

	return (
		<div key={desktopImgSrc} className={`relative custom-avatar ${wrapClassName}`}>
			{showLoader && <BounceLoader className="loading-component__m0-auto" />}

			{lazy ? (
				<InView
					as="div"
					threshold={0}
					rootMargin={`${offset}px`}
					onChange={(inView) => {
						if (inView) setIsVisible(true);
					}}
				>
					{isVisible && renderImage()}
				</InView>
			) : (
				renderImage()
			)}
		</div>
	);
};

export default CustomAvatar;
